import { ClipboardCheck, Download, Eye, FileText, FolderInput, MoreVertical, Trash2, Users, CheckCircle2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { formatFileSize, looksLikePdf } from '../../services/files/fileUtils';
import type { DossierFile } from '../../services/files/filesService';
import { useAuthStore } from '../../store/useAuthStore';
import {
  VISA_FILE_ACTION_LABEL,
  visaFileMenu,
  type VisaFileAction,
} from '../../services/signature/circuitRevision';
import {
  CIME_DOSSIER_FILE_DRAG,
  CIME_DOSSIER_FILE_DRAG_ALT,
  serializeInternalFileDrag,
} from '../../lib/fileDrop';
import { useFileTreeDrop } from './FileTreeDropContext';
import {
  FaArrowUpFromBracket,
  FaDownload,
  FaEnvelope,
  FaFile,
  FaFileExcel,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
  FaWandMagicSparkles,
  type AppIcon,
} from '../ui/FaIcons';

import { formatAppDate } from '../../lib/dateFormat';
interface Props {
  file: DossierFile;
  onPreview: (file: DossierFile) => void;
  onDownload: (file: DossierFile) => void;
  onDelete: (file: DossierFile) => void;
  onMove: (file: DossierFile) => void;
  onToggleVisibility: (file: DossierFile) => void;
  canEdit: boolean;
  canCreateCircuit?: boolean;
  onStartCircuit?: (file: DossierFile, kind?: 'visa' | 'validation') => void;
  onOpenCircuit?: (file: DossierFile) => void;
  depth?: number;
  /** Mode tableau de l'onglet Documents : case à cocher, Source, Client, Modifié. */
  table?: {
    selected: boolean;
    onSelect: (file: DossierFile) => void;
    /** Sous-dossier d'origine, affiché sous le titre. */
    folderName?: string | null;
  };
}

export function FileRow({
  file, onPreview, onDownload, onDelete, onMove, onToggleVisibility, canEdit,
  canCreateCircuit, onStartCircuit, onOpenCircuit, depth = 0, table,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const skipClickAfterDrag = useRef(false);
  const dropCtx = useFileTreeDrop();
  const draggable = canEdit && !!dropCtx?.enabled;
  const collab = useAuthStore((s) => s.collaborator);
  const visaActions = visaFileMenu({
    linkedStatus: file.signature_circuit?.status
      ?? (file.signature_circuit_id ? 'in_progress' : null),
    isInitiator: file.signature_circuit?.initiator_id === collab?.id,
    isAdmin: collab?.role === 'admin',
  });

  function runVisaAction(action: VisaFileAction) {
    setMenuOpen(false);
    if (action === 'demander') onStartCircuit?.(file, 'visa');
    else if (action === 'demander_validation') onStartCircuit?.(file, 'validation');
    else onOpenCircuit?.(file);
  }

  return (
    <div
      className={
        (table
          ? FILE_TABLE_GRID + ' py-2 border-b border-neutral-100 cursor-pointer ' +
            (table.selected ? 'bg-[#f3f7f5] ' : 'hover:bg-neutral-50 ')
          : 'flex items-center gap-3 pr-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 cursor-pointer ') +
        (dragging ? 'opacity-50 cursor-grabbing ' : '')
      }
      style={table ? undefined : { paddingLeft: 16 + depth * 20 }}
      draggable={draggable}
      onClick={() => {
        if (skipClickAfterDrag.current) {
          skipClickAfterDrag.current = false;
          return;
        }
        onPreview(file);
      }}
      onDragStart={(e) => {
        if (!draggable) return;
        const raw = serializeInternalFileDrag({ fileId: file.id, fromFolderId: file.folder_id });
        e.dataTransfer.setData(CIME_DOSSIER_FILE_DRAG, raw);
        e.dataTransfer.setData(CIME_DOSSIER_FILE_DRAG_ALT, raw);
        e.dataTransfer.setData('text/plain', raw);
        e.dataTransfer.effectAllowed = 'move';
        setDragging(true);
      }}
      onDragEnd={() => {
        setDragging(false);
        skipClickAfterDrag.current = true;
        window.setTimeout(() => {
          skipClickAfterDrag.current = false;
        }, 50);
      }}
    >
      {table ? (
        <>
          <button
            type="button"
            role="checkbox"
            aria-checked={table.selected}
            aria-label={`Sélectionner ${file.filename}`}
            onClick={(e) => {
              e.stopPropagation();
              table.onSelect(file);
            }}
            className={
              'grid h-4 w-4 place-items-center rounded border-[1.5px] ' +
              (table.selected ? 'border-cime-primary bg-cime-primary text-white' : 'border-neutral-300 bg-white')
            }
          >
            {table.selected ? (
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
                <path d="M2.2 6.2 4.8 8.8 9.8 3.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : null}
          </button>
          <div className="flex min-w-0 items-center gap-3">
            <FileKindBadge filename={file.filename} mime={file.mime} />
            <div className="min-w-0 leading-tight">
              <div className="flex items-center gap-2">
                <span className="truncate text-sm font-medium text-cime-text-primary">{file.filename}</span>
                {file.signed_storage_path ? <StateTag>Signé</StateTag> : null}
                {!file.signed_storage_path
                  && file.signature_circuit?.status === 'completed'
                  && file.signature_circuit.kind === 'validation' ? <StateTag>Validé</StateTag> : null}
              </div>
              <div className="truncate text-xs text-neutral-400">
                {fileExt(file.filename).toUpperCase() || 'Fichier'} · {formatFileSize(file.size)}
                {table.folderName ? ` · ${table.folderName}` : ''}
              </div>
            </div>
          </div>
          <FileSource source={file.source} />
          <span title={file.visibility === 'shared_client' ? 'Partagé avec le client' : 'Interne au cabinet'}>
            {file.visibility === 'shared_client' ? (
              <Eye className="h-4 w-4 text-cime-gold" aria-label="Partagé avec le client" />
            ) : (
              <span className="text-neutral-300">—</span>
            )}
          </span>
          <span className="whitespace-nowrap text-[13px] text-cime-text-primary">
            {formatAppDate(file.created_at, { year: false })}
          </span>
          <button
            type="button"
            title="Télécharger"
            aria-label={`Télécharger ${file.filename}`}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(file);
            }}
            className="grid h-7 w-7 place-items-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-cime-primary"
          >
            <FaDownload className="h-3.5 w-3.5" aria-hidden />
          </button>
        </>
      ) : (
        <>
          <FileText className="w-5 h-5 text-neutral-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-neutral-900 truncate">{file.filename}</div>
            <div className="text-xs text-neutral-500">
              {formatFileSize(file.size)} · {formatAppDate(file.created_at)}
            </div>
          </div>
          {file.signed_storage_path && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
              <CheckCircle2 className="w-3 h-3" /> Signé
            </span>
          )}
          {!file.signed_storage_path
            && file.signature_circuit?.status === 'completed'
            && file.signature_circuit.kind === 'validation' && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
              <CheckCircle2 className="w-3 h-3" /> Validé
            </span>
          )}
          {file.visibility === 'shared_client' && (
            <span className="flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
              <Users className="w-3 h-3" /> Client
            </span>
          )}
        </>
      )}
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="p-1.5 rounded hover:bg-neutral-200"
          aria-label="Actions"
        >
          <MoreVertical className="w-4 h-4 text-neutral-600" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 z-10 min-w-[160px] rounded-md border border-neutral-200 bg-white shadow-lg py-1">
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onPreview(file); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
            >
              <Eye className="w-3.5 h-3.5" /> Aperçu
            </button>
            <button
              type="button"
              onClick={() => { setMenuOpen(false); onDownload(file); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
            >
              <Download className="w-3.5 h-3.5" /> Télécharger
            </button>
            {canEdit && (
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onMove(file); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
              >
                <FolderInput className="w-3.5 h-3.5" /> Déplacer
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onToggleVisibility(file); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
              >
                <Users className="w-3.5 h-3.5" />
                {file.visibility === 'shared_client' ? 'Rendre interne' : 'Partager avec le client'}
              </button>
            )}
            {canCreateCircuit && looksLikePdf(file.mime, file.filename) && visaActions.map((action) => (
              <button
                key={action}
                type="button"
                onClick={() => runVisaAction(action)}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left"
              >
                <ClipboardCheck className="w-3.5 h-3.5" /> {VISA_FILE_ACTION_LABEL[action]}
              </button>
            ))}
            {canEdit && (
              <button
                type="button"
                onClick={() => { setMenuOpen(false); onDelete(file); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm hover:bg-neutral-50 text-left text-red-600"
              >
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Colonnes du tableau de l'onglet Documents : case, titre, source, client, modifié, menu. */
export const FILE_TABLE_GRID =
  'grid grid-cols-[16px_minmax(0,1fr)_96px_56px_84px_28px_32px] items-center gap-3.5 px-[18px]';

const FILE_SOURCE: Record<string, { label: string; icon: AppIcon }> = {
  upload: { label: 'Import', icon: FaArrowUpFromBracket },
  email: { label: 'Outlook', icon: FaEnvelope },
  ai_generated: { label: 'Généré', icon: FaWandMagicSparkles },
};

function FileSource({ source }: { source: string }) {
  const meta = FILE_SOURCE[source];
  const Icon = meta?.icon;
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-cime-text-muted">
      {Icon ? <Icon className="h-3 w-3 flex-shrink-0" aria-hidden /> : null}
      <span className="truncate">{meta?.label ?? source}</span>
    </span>
  );
}

function fileExt(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

export function FileKindBadge({ filename, mime }: { filename: string; mime: string }) {
  const ext = fileExt(filename);
  const [tone, Icon]: [string, AppIcon] =
    mime === 'application/pdf' || ext === 'pdf'
      ? ['bg-[#fdece6] text-[#c8431f]', FaFilePdf]
      : ['doc', 'docx', 'odt'].includes(ext)
        ? ['bg-[#e6eefb] text-[#2b5aa8]', FaFileWord]
        : ['xls', 'xlsx', 'csv'].includes(ext)
          ? ['bg-[#e8f0ec] text-[#2f7a55]', FaFileExcel]
          : mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
            ? ['bg-[#f3ecfa] text-[#7a4fb0]', FaFileImage]
            : ['bg-neutral-100 text-cime-text-muted', FaFile];
  return (
    <span className={'grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg ' + tone}>
      <Icon className="h-4 w-4" aria-hidden />
    </span>
  );
}

function StateTag({ children }: { children: string }) {
  return (
    <span className="inline-flex flex-shrink-0 items-center rounded bg-green-50 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-green-700">
      {children}
    </span>
  );
}
